// LevelRangeInput — ActivityWizard Steg 2. 10-rutors nivåväljare (Livfull, IMG_1215),
// skala 1-10. Kant-selektion: ett klick utvidgar/flyttar närmaste kant → ett alltid
// sammanhängande [minLevel, maxLevel]-spann. Sätter EXAKT samma minLevel/maxLevel-fält
// som den tidigare Från/Till-varianten (field-ekvivalent) via onMinChange/onMaxChange —
// bara presentationen bytt. Spannet är alltid giltigt (min ≤ max), så det gamla
// min>max-felläget (level-error) finns inte längre (se tasks/todo.md, skuld).

import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface LevelRangeInputProps {
  minLevel: number | null;
  maxLevel: number | null;
  onMinChange: (value: number) => void;
  onMaxChange: (value: number) => void;
}

const clampLevel = (v: number): number => Math.max(1, Math.min(10, Math.round(v) || 1));
const LEVELS = Array.from({ length: 10 }, (_, i) => i + 1);

export default function LevelRangeInput({ minLevel, maxLevel, onMinChange, onMaxChange }: LevelRangeInputProps) {
  const { t } = useTranslation("admin");
  const lo = clampLevel(minLevel ?? 1);
  const hi = clampLevel(maxLevel ?? 10);

  // Tap a box: extend or move the nearer edge. Exactly one edge changes per click, so at
  // most one of onMinChange/onMaxChange fires. The result is always a contiguous [lo, hi].
  const pick = (v: number) => {
    let nextLo = lo;
    let nextHi = hi;
    if (v < lo) nextLo = v;
    else if (v > hi) nextHi = v;
    else if (v - lo <= hi - v) nextLo = v;
    else nextHi = v;
    if (nextLo !== lo) onMinChange(clampLevel(nextLo));
    if (nextHi !== hi) onMaxChange(clampLevel(nextHi));
  };

  const rangeLabel = lo === hi ? String(lo) : `${lo}–${hi}`;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-10 gap-1.5">
        {LEVELS.map((v) => {
          const inRange = v >= lo && v <= hi;
          const edge = v === lo || v === hi;
          return (
            <button
              key={v}
              type="button"
              aria-pressed={inRange}
              data-testid={`level-box-${v}`}
              onClick={() => pick(v)}
              className={cn(
                "flex aspect-square items-center justify-center rounded-lg border text-sm font-semibold transition-colors",
                edge
                  ? "livfull-pill-selected"
                  : inRange
                    ? "border-primary/30 bg-primary/5 text-primary"
                    : "border-border text-muted-foreground hover:border-primary/50",
              )}
            >
              {v}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{t("activityWizard.step3.levelBeginner", { defaultValue: "Nybörjare" })}</span>
        <span className="font-medium text-foreground">
          {t("activityWizard.step3.levelRangeLabel", { range: rangeLabel, defaultValue: `Nivå ${rangeLabel}` })}
        </span>
        <span>{t("activityWizard.step3.levelElite", { defaultValue: "Elit" })}</span>
      </div>
    </div>
  );
}
